import type {
    AiChatColumnMeta,
    AppRegistryColumn,
    AppRegistryPage,
    PageAiMeta,
} from '@plato/app-meta/ai-meta';
import { useMemo, useState } from 'react';

type ColumnSource = {
    id?: unknown;
    accessorKey?: unknown;
    header?: unknown;
    meta?: { aiChat?: AiChatColumnMeta };
    columns?: readonly ColumnSource[];
};

type IdentifiedColumn = {
    identity: string;
    column: AppRegistryColumn;
};

const noColumns: readonly ColumnSource[] = [];

export function usePageAiMeta(
    meta: PageAiMeta,
    columns: readonly ColumnSource[] = noColumns,
): AppRegistryPage {
    const { title, ...metaWithoutTitle } = meta;
    const [staticMeta] = useState(metaWithoutTitle);

    return useMemo(() => {
        const { path, description, ...accessRule } = staticMeta;
        return {
            path,
            params: paramsOf(path),
            title,
            description,
            ...accessRule,
            columns: distinctColumns(registryColumnsOf(columns)),
        };
    }, [staticMeta, title, columns]);
}

function paramsOf(path: string): string[] {
    return path
        .split('/')
        .filter((segment) => segment.startsWith(':'))
        .map((segment) => segment.slice(1));
}

function registryColumnsOf(columns: readonly ColumnSource[]): IdentifiedColumn[] {
    return columns.flatMap((column) => [
        ...(typeof column.accessorKey === 'string'
            ? [
                  {
                      identity: typeof column.id === 'string' ? column.id : column.accessorKey,
                      column: {
                          accessorKey: column.accessorKey,
                          ...(typeof column.header === 'string' ? { label: column.header } : {}),
                          ...column.meta?.aiChat,
                      },
                  },
              ]
            : []),
        ...(column.columns ? registryColumnsOf(column.columns) : []),
    ]);
}

function distinctColumns(columns: IdentifiedColumn[]): AppRegistryColumn[] {
    return [
        ...new Map(
            columns.map(({ identity, column }) => [`${identity}\u0000${JSON.stringify(column)}`, column]),
        ).values(),
    ];
}
